// =============================================
// File: src/hooks/useRRTrend.js
// Purpose: Encapsulate fetching + day-bucketing of RR trend
// =============================================
import { useMemo } from "react";
import { useRecentRR } from "../hooks/useRecentRR"; // keep your existing hook

export function useRRTrend({
  uid,
  baseName,
  date,
  windowDays = 30,
  limit = undefined,
  tag = "breatheultra",
  repo = "pdx-recording-exertion-recovery",
  variant = "exertion", // allow caller to override
}) {
  const { loading, data: points } = useRecentRR({
    uid,
    tag,
    variant,
    repo,
    currentBaseName: baseName,
    windowDays,
    limit,
    debug: true,
    anchorDate: date, // <-- anchor the window to selected activity day
    includeCurrent: true, // <-- include the selected activity itself
  });

  // Build series and day-bucketed labels/values/minMax for sparkline UI
  // + a compact history object for AI
  const { labels, values, minMax, history } = useMemo(() => {
    const buckets = new Map(); // YYYY-MM-DD -> { tsLatest, latest, min, max, sum, count }

    const keyFromLocal = (ts) => {
      const d = new Date(ts); // local
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`; // local YYYY-MM-DD
    };

    const pushPoint = (ts, value) => {
      if (!Number.isFinite(ts) || !Number.isFinite(value)) return;
      const key = keyFromLocal(ts);
      const b = buckets.get(key) || {
        tsLatest: 0,
        latest: value,
        min: Infinity,
        max: -Infinity,
        count: 0,
        sum: 0,
      };
      if (ts > b.tsLatest) {
        b.tsLatest = ts;
        b.latest = value;
      }
      b.min = Math.min(b.min, value);
      b.max = Math.max(b.max, value);
      b.sum += value;
      b.count += 1;
      buckets.set(key, b);
    };

    // Use ONLY Firestore points (no injected current value)
    for (const p of points || []) pushPoint(p?.ts, p?.rr);

    const rows = Array.from(buckets.entries())
      .map(([key, b]) => ({
        key,
        ...b,
        avg: b.count ? b.sum / b.count : undefined,
      }))
      .sort((a, b) => a.tsLatest - b.tsLatest);

    // Labels from the BUCKET KEY (local day), not tsLatest
    const labels = rows.map((r) => {
      const [y, m, d] = r.key.split("-");
      return `${d}/${m}`;
    });
    // For the line, use the daily AVERAGE
    const values = rows.map((r) => Math.round(r.avg));
    // Bars show true min/max for each day (including the selected day)
    const minMax = rows.map((r) => ({ min: r.min, max: r.max }));

    // Compact RR history for AI (more absolute label)
    const history = rows
      .map((r) => ({
        label: r.key, // YYYY-MM-DD, stable for AI
        rrAvgBpm: Number(r.avg),
        rrMinBpm: Number(r.min),
        rrMaxBpm: Number(r.max),
      }))
      .filter((p) => Number.isFinite(p.rrBpm));

    return { labels, values, minMax, history };
  }, [points]);

  return { loading, labels, values, minMax, history };
}
